import { useTranslation } from "react-i18next";
import type { EquityChangesResponse } from "@/features/accounting/financial-reports/types";

interface Props {
  reportYear: number;
  orgInfo: EquityChangesResponse["org_info"];
  userIin?: string | null;
}

export default function EquityChangesFormHeader({ reportYear, orgInfo, userIin }: Props) {
  const { t } = useTranslation("FinancialReportPage");

  const boilerplate = [
    { key: "submitted", label: t("equityChanges.form.submittedLabel"), text: t("equityChanges.form.submittedText") },
    { key: "resource", label: null, text: t("equityChanges.form.resource") },
    { key: "index", label: t("equityChanges.form.indexLabel"), text: t("equityChanges.form.indexText") },
    { key: "periodicity", label: t("equityChanges.form.periodicityLabel"), text: t("equityChanges.form.periodicityText") },
    { key: "audience", label: t("equityChanges.form.audienceLabel"), text: t("equityChanges.form.audienceText") },
    { key: "deadline", label: t("equityChanges.form.deadlineLabel"), text: t("equityChanges.form.deadlineText") },
  ];

  const fields = [
    { key: "orgName", label: t("equityChanges.form.fields.orgName"), value: orgInfo.name },
    { key: "activityType", label: t("equityChanges.form.fields.activityType"), value: orgInfo.activity_type },
    { key: "headcount", label: t("equityChanges.form.fields.headcount"), value: orgInfo.average_headcount ?? "" },
    { key: "legalAddress", label: t("equityChanges.form.fields.legalAddress"), value: orgInfo.address ?? "" },
    { key: "bin", label: t("equityChanges.form.fields.bin"), value: orgInfo.bin ?? "" },
    { key: "iin", label: t("equityChanges.form.fields.iin"), value: userIin ?? "" },
  ];

  return (
    <div className="flex flex-col gap-6 mb-8">
      <div className="flex flex-col items-end gap-4 text-body-regular-xs content-base-secondary text-right">
        <p className="whitespace-pre-line">{t("equityChanges.form.appendix1")}</p>
        <p className="whitespace-pre-line">{t("equityChanges.form.appendix2")}</p>
        <p>{t("equityChanges.form.formWord")}</p>
      </div>

      <div className="flex flex-col items-center gap-3">
        <p className="text-display-2xs content-base-primary uppercase text-center">{t("equityChanges.title")}</p>
        <div className="flex items-center gap-2 text-body-regular-md content-base-primary">
          <span>{t("equityChanges.form.reportPeriod")}</span>
          <span className="border-b surface-base-stroke text-body-bold-md content-base-primary px-6 pb-0.5">
            {reportYear}
          </span>
          <span>{t("equityChanges.form.yearWord")}</span>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        {boilerplate.map(function renderBoilerplate(line) {
          return (
            <p key={line.key} className="text-body-regular-sm content-base-primary">
              {line.label && <span className="text-body-bold-sm content-base-primary">{line.label} </span>}
              {line.text}
            </p>
          );
        })}
        <p className="text-body-regular-sm italic content-base-secondary">{t("equityChanges.form.note")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        {fields.map(function renderField(field) {
          return (
            <div key={field.key} className="flex items-baseline gap-2">
              <span className="text-body-regular-md content-base-secondary whitespace-nowrap">{field.label}:</span>
              <span className="flex-1 border-b surface-base-stroke text-body-bold-md content-base-primary pb-0.5 min-h-[1.5rem]">
                {field.value}
              </span>
            </div>
          );
        })}
      </div>

      <p className="text-center text-body-regular-md content-base-primary">
        {t("equityChanges.form.periodEnding", { year: reportYear })}
      </p>
    </div>
  );
}
